import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { HiOutlineCheck, HiOutlineChevronDown } from "react-icons/hi2";
import { DOC_ISSUE_LABELS, RISK_BADGE, RISK_EDIT_OPTIONS, RISK_LEVELS } from "../utils/constants";

const CareWatchRiskBadge = ({ risk, issue, label, mode = "risk" }) => {
  if (mode === "issue") {
    return (
      <span className="care-watch-issue-badge inline-flex h-[22px] items-center justify-center rounded-full px-2.5 text-[10px] font-semibold whitespace-nowrap">
        {/* `label` names the finding, as the mobile detail header does. The
            table passes no label and keeps the bucket wording, which is what
            its KPI tiles are counted and filtered by. */}
        {label || DOC_ISSUE_LABELS[issue] || "Issue"}
      </span>
    );
  }

  const badge = RISK_BADGE[risk];
  if (!badge) return null;

  return (
    <span
      className="inline-flex h-[22px] items-center justify-center rounded-full px-2.5 text-[10px] font-semibold whitespace-nowrap"
      style={{ background: badge.bg, color: badge.text }}
    >
      {badge.label}
    </span>
  );
};

const riskValue = (risk) => {
  if (risk === "medium") return RISK_LEVELS.MODERATE;
  return RISK_BADGE[risk] ? risk : RISK_LEVELS.LOW;
};

export const CareWatchRiskControl = ({ risk, disabled = false, onChange }) => {
  const value = riskValue(risk);
  const badge = RISK_BADGE[value];
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  const place = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 148;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    setPosition({ top: rect.bottom + 6, left, width });
  };

  useEffect(() => {
    if (!open) return undefined;
    place();
    const close = () => setOpen(false);
    const onKey = (event) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const stop = (event) => event.stopPropagation();

  return (
    <span className="relative inline-flex" onClick={stop} onMouseDown={stop}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Change risk"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        className={`care-watch-risk-menu${open ? " is-open" : ""}`}
        style={{ background: badge.bg, color: badge.text }}
        onClick={(event) => {
          stop(event);
          if (disabled) return;
          setOpen((current) => !current);
        }}
      >
        {disabled ? "Saving…" : badge.label}
        <HiOutlineChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
      </button>
      {open && position
        ? ReactDOM.createPortal(
            <>
              <button
                type="button"
                aria-label="Close risk menu"
                className="care-watch-risk-menu-backdrop"
                onMouseDown={stop}
                onClick={(event) => {
                  stop(event);
                  setOpen(false);
                }}
              />
              <div
                role="listbox"
                aria-label="Risk"
                className="care-watch-risk-menu-panel"
                style={{ top: position.top, left: position.left, width: position.width }}
                onMouseDown={stop}
                onClick={stop}
              >
                {RISK_EDIT_OPTIONS.map((option) => {
                  const optionBadge = RISK_BADGE[option.value];
                  const selected = option.value === value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`care-watch-risk-option${selected ? " is-selected" : ""}`}
                      style={{ background: optionBadge.bg, color: optionBadge.text }}
                      onClick={(event) => {
                        stop(event);
                        setOpen(false);
                        if (option.value !== value) onChange(option.value);
                      }}
                    >
                      <span>{optionBadge.label}</span>
                      {selected ? (
                        <HiOutlineCheck className="care-watch-risk-option-check" aria-hidden="true" />
                      ) : (
                        <span className="care-watch-risk-option-check" aria-hidden="true" />
                      )}
                    </button>
                  );
                })}
              </div>
            </>,
            document.body
          )
        : null}
    </span>
  );
};

export default CareWatchRiskBadge;
